import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Slider,
  Alert,
  Divider,
  Table,
  Autocomplete,
  TextField,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper as MuiPaper,
  Stepper,
  Step,
  StepLabel,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import axiosInstance from "../../axios/axiosInstance";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const skillLevels = ["Beginner", "Intermediate", "Advanced", "Expert"];

const tabDefs = [
  {
    value: "must",
    label: "Must have",
    skillKey: "must_have_skills",
    specificKey: "must_have_specific_skills",
  },
  {
    value: "should",
    label: "Should have",
    skillKey: "should_have_skills",
    specificKey: "should_have_specific_skills",
  },
  {
    value: "could",
    label: "Could have",
    skillKey: "could_have_skills",
    specificKey: "could_have_specific_skills",
  },
];

const SetSkills = ({
  formData,
  skillsData: parentSkillsData,
  setSkillsData: setParentSkillsData,
  selectedSoftSkills: parentSelectedSoftSkills,
  setSelectedSoftSkills: setParentSelectedSoftSkills,
  setSoftSkillsNames,
  onBack,
  onNext,
  languages: parentLanguages = [],
  setLanguages: setParentLanguages,
  formLanguages = [],
  setFormLanguages,
  languagesOptions = [],
  fluencyLevels = [],
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState(parentSkillsData || {});
  const [selectedSoftSkill, setSelectedSoftSkill] = useState(
    parentSelectedSoftSkills || [],
  );

  const navigate = useNavigate();
  const [tab, setTab] = useState("must");
  const [error, setError] = useState("");
  const [softSkills, setSoftSkills] = useState([]);
  const [loadingSoftSkills, setLoadingSoftSkills] = useState(false);
  const [languagesList, setLanguagesList] = useState(parentLanguages || []);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedFluency, setSelectedFluency] = useState("");

  // Add skillsData as local state
  const [skillsData, setSkillsData] = useState(null);

  // Sync languages with parent
  useEffect(() => {
    if (parentLanguages.length > 0) {
      setLanguagesList(parentLanguages);
    }
  }, [parentLanguages]);

  const jobFunctionId = formData.functions;
  const jobRoleId = formData.position;

  const fetchSoftSkills = async () => {
    try {
      setLoadingSoftSkills(true);
      const res = await axiosInstance.get(`api/job-postings/soft-skills/list`);
      setSoftSkills(res.data || []);
      setSoftSkillsNames(res.data || []);
    } catch (error) {
      console.error("Error fetching soft skills:", error);
    } finally {
      setLoadingSoftSkills(false);
    }
  };

  useEffect(() => {
    fetchSoftSkills();
  }, []);

  const fetchSkills = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get(
        `api/profile/job-role-skills/${jobFunctionId}/${jobRoleId}`,
      );
      if (!res.data) throw new Error("Failed to fetch skills");
      setSkillsData(res.data);
    } catch (err) {
      setError("Could not load skills.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, [jobFunctionId, jobRoleId]);

  const createSkillKey = (category, mainSkill) => {
    return `${category}|${mainSkill}`;
  };

  const parseSkillKey = (key) => {
    const [category, mainSkill] = key.split("|");
    return { category, mainSkill };
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const getSelectedCountByCategory = (category) => {
    return Object.keys(selectedSkills).filter((key) =>
      key.startsWith(`${category}|`),
    ).length;
  };

  const getAllSelectedSkillNames = () => {
    return Object.keys(selectedSkills).map((key) => key.split("|")[1]);
  };

  const handleMainSkillToggle = (category, mainSkill, subSkills) => {
    const skillKey = createSkillKey(category, mainSkill);

    setSelectedSkills((prev) => {
      const newState = { ...prev };
      const isCurrentlySelected = !!prev[skillKey];

      if (isCurrentlySelected) {
        delete newState[skillKey];
        return newState;
      }

      const countInCategory = getSelectedCountByCategory(category);
      if (countInCategory >= 3) {
        toast.warning(`You can select up to 3 ${category}-have skills only.`);
        return prev;
      }

      const allSelected = getAllSelectedSkillNames();
      if (allSelected.includes(mainSkill)) {
        toast.warning(
          `"${mainSkill}" is already selected in another category.`,
        );
        return prev;
      }

      newState[skillKey] = {
        level: 2,
        subSkills: [],
      };

      return newState;
    });
  };

  const handleSubSkillToggle = (category, mainSkill, subSkill) => {
    const skillKey = createSkillKey(category, mainSkill);

    setSelectedSkills((prev) => {
      const newState = { ...prev };

      if (!newState[skillKey]) return prev;

      const currentSubSkills = newState[skillKey].subSkills || [];
      // Support both old string array and new object array for backward compatibility during transition
      const isObject =
        currentSubSkills.length > 0 && typeof currentSubSkills[0] === "object";

      const exists = isObject
        ? currentSubSkills.some((s) => s.name === subSkill)
        : currentSubSkills.includes(subSkill);

      if (exists) {
        newState[skillKey] = {
          ...newState[skillKey],
          subSkills: isObject
            ? currentSubSkills.filter((s) => s.name !== subSkill)
            : currentSubSkills.filter((s) => s !== subSkill),
        };
      } else {
        newState[skillKey] = {
          ...newState[skillKey],
          subSkills: [
            ...currentSubSkills,
            { name: subSkill, level: newState[skillKey].level || 2 },
          ],
        };
      }

      return newState;
    });
  };

  const handleSubSkillLevelChange = (
    category,
    mainSkill,
    subSkillName,
    newLevel,
  ) => {
    const skillKey = createSkillKey(category, mainSkill);

    setSelectedSkills((prev) => {
      const newState = { ...prev };
      if (!newState[skillKey]) return prev;

      newState[skillKey] = {
        ...newState[skillKey],
        subSkills: (newState[skillKey].subSkills || []).map((s) => {
          if (typeof s === "string") return { name: s, level: newLevel };
          return s.name === subSkillName ? { ...s, level: newLevel } : s;
        }),
      };

      return newState;
    });
  };

  const handleLevelChange = (category, mainSkill, newLevel) => {
    const skillKey = createSkillKey(category, mainSkill);

    setSelectedSkills((prev) => ({
      ...prev,
      [skillKey]: {
        ...prev[skillKey],
        level: newLevel,
      },
    }));
  };

  const handleDeselectSkill = (category, mainSkill) => {
    const countInCategory = getSelectedCountByCategory(category);
    if (countInCategory <= 1) {
      toast.warning(
        `At least one skill is required in ${category}-have category.`,
      );
      return;
    }
    const skillKey = createSkillKey(category, mainSkill);
    setSelectedSkills((prev) => {
      const newState = { ...prev };
      delete newState[skillKey];
      return newState;
    });
  };

  const currentTabDef = tabDefs.find((t) => t.value === tab);

  const getSelectedGroupedSkills = () => {
    if (!skillsData) return {};

    const grouped = {};

    tabDefs.forEach(({ value, skillKey, specificKey }) => {
      grouped[value] = [];

      (skillsData[skillKey] || []).forEach((mainSkill, idx) => {
        const key = createSkillKey(value, mainSkill);
        const selection = selectedSkills[key];

        if (selection) {
          grouped[value].push({
            mainSkill,
            level: selection.level,
            subSkills: selection.subSkills || [],
            availableSubSkills: skillsData[specificKey]?.[idx] || [],
          });
        }
      });
    });

    return grouped;
  };

  const selectedGrouped = getSelectedGroupedSkills();

  const totalSelectedSkills = Object.values(selectedGrouped || {}).reduce(
    (count, group) => count + (group?.length || 0),
    0,
  );

  const hasCategorySelected = (category) => {
    return Object.keys(selectedSkills).some((key) =>
      key.startsWith(`${category}|`),
    );
  };

  const allCategoriesSelected = () => {
    const categories = ["must", "should", "could"];
    const remaining = categories
      .filter((cat) => !hasCategorySelected(cat))
      .map((cat) => cat.charAt(0).toUpperCase() + cat.slice(1));

    const allSelected = remaining.length === 0;
    return { allSelected, remaining };
  };

  const handleNext = () => {
    if (selectedSoftSkill.length < 0) {
      toast.warning("Please select at least one soft skill.");
      return;
    }
    const { allSelected, remaining } = allCategoriesSelected();
    if (!allSelected) {
      toast.warning(
        `Please select at least one skill under each of the following categories: ${
          remaining.join(", ") || ""
        }.`,
      );
      return;
    }
    if (languagesList.length === 0) {
      toast.warning("Please select at least one language.");
      return;
    }
    setParentSkillsData(selectedSkills);
    setParentSelectedSoftSkills(selectedSoftSkill);
    onNext();
  };

  // Usage

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 6 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/corporate/job-postings")}
          sx={{ mb: 2 }}
        >
          Back to Job Postings
        </Button>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Stepper activeStep={1} alternativeLabel sx={{ mb: 4 }}>
            <Step key={0}>
              <StepLabel>Create a role</StepLabel>
            </Step>
            <Step key={1}>
              <StepLabel>Set skills</StepLabel>
            </Step>
            <Step key={2}>
              <StepLabel>Company type & culture Fit</StepLabel>
            </Step>
            <Step key={3}>
              <StepLabel>Define interview process</StepLabel>
            </Step>
            <Step key={4}>
              <StepLabel>Review & Publish</StepLabel>
            </Step>
          </Stepper>

          <Typography variant="h5" sx={{ mb: 2 }}>
            Skills
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Select Skills for this job
          </Typography>

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  multiple
                  options={softSkills}
                  getOptionLabel={(option) => option.name}
                  value={softSkills.filter((s) =>
                    selectedSoftSkill.includes(s.id),
                  )}
                  onChange={(event, newValue) => {
                    if (newValue.length <= 5) {
                      setSelectedSoftSkill(newValue.map((item) => item.id));
                    } else {
                      toast.warning("You can select up to 5 soft skills only.");
                    }
                  }}
                  loading={loadingSoftSkills}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Soft Skills*"
                      placeholder="Soft Skills"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingSoftSkills ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              <Button
                variant="outlined"
                onClick={handleOpenDialog}
                sx={{ mb: 3 }}
              >
                {totalSelectedSkills > 0
                  ? "Edit Technical Skills *"
                  : "Add Technical Skills *"}
              </Button>

              <Box sx={{ mt: 3 }}>
                {tabDefs.map(
                  ({ value, label }) =>
                    selectedGrouped[value] &&
                    selectedGrouped[value].length > 0 && (
                      <Box key={value} sx={{ mb: 6 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 1, fontWeight: 700 }}
                        >
                          {label}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <TableContainer
                          component={MuiPaper}
                          sx={{ boxShadow: "none" }}
                        >
                          <Table sx={{ minWidth: 500 }}>
                            <TableBody>
                              {selectedGrouped[value].map(
                                ({
                                  mainSkill,
                                  level,
                                  subSkills,
                                  availableSubSkills,
                                }) => (
                                  <TableRow key={mainSkill}>
                                    <TableCell
                                      sx={{
                                        fontWeight: 600,
                                        width: "20%",
                                        position: "relative",
                                        textAlign: "center",
                                      }}
                                    >
                                      <Checkbox
                                        checked={true}
                                        onChange={() =>
                                          handleDeselectSkill(value, mainSkill)
                                        }
                                        size="small"
                                        sx={{
                                          color: "#6c5ce7",
                                          "&.Mui-checked": {
                                            color: "#6c5ce7",
                                          },
                                          p: 0,
                                          position: "absolute",
                                          top: 8,
                                          left: 8,
                                        }}
                                      />
                                      {mainSkill}
                                    </TableCell>
                                    <TableCell>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{ mb: 1, fontWeight: 600 }}
                                        >
                                          Skill Level
                                        </Typography>
                                        <Slider
                                          value={level}
                                          min={1}
                                          max={4}
                                          step={1}
                                          marks={skillLevels.map(
                                            (label, idx) => ({
                                              value: idx + 1,
                                              label,
                                            }),
                                          )}
                                          onChange={(_, newValue) =>
                                            handleLevelChange(
                                              value,
                                              mainSkill,
                                              newValue,
                                            )
                                          }
                                          valueLabelDisplay="off"
                                          sx={{
                                            mb: 1,
                                            width: "80%", // Reduce slider width
                                          }}
                                        />

                                        {availableSubSkills.length > 0 && (
                                          <Box sx={{ mt: 2 }}>
                                            <Typography
                                              variant="caption"
                                              sx={{ fontWeight: 600 }}
                                            >
                                              Sub-skills
                                            </Typography>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                                mt: 1,
                                              }}
                                            >
                                              {availableSubSkills.map(
                                                (subSkill) => {
                                                  const subSkillData =
                                                    subSkills.find((s) =>
                                                      typeof s === "string"
                                                        ? s === subSkill
                                                        : s.name === subSkill,
                                                    );
                                                  const isChecked =
                                                    !!subSkillData;
                                                  const subSkillLevel =
                                                    typeof subSkillData ===
                                                    "object"
                                                      ? subSkillData.level
                                                      : level || 1;

                                                  return (
                                                    <Box
                                                      key={subSkill}
                                                      sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        mb: 1,
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <FormControlLabel
                                                        control={
                                                          <Checkbox
                                                            checked={isChecked}
                                                            onChange={() =>
                                                              handleSubSkillToggle(
                                                                value,
                                                                mainSkill,
                                                                subSkill,
                                                              )
                                                            }
                                                            size="small"
                                                          />
                                                        }
                                                        label={
                                                          <Typography variant="body2">
                                                            {subSkill}
                                                          </Typography>
                                                        }
                                                      />
                                                      {isChecked && (
                                                        <Box
                                                          sx={{
                                                            pl: 4,
                                                            pr: 2,
                                                            width: "70%",
                                                          }}
                                                        >
                                                          <Slider
                                                            value={
                                                              subSkillLevel
                                                            }
                                                            min={1}
                                                            max={4}
                                                            step={1}
                                                            marks={skillLevels.map(
                                                              (label, idx) => ({
                                                                value: idx + 1,
                                                                label,
                                                              }),
                                                            )}
                                                            onChange={(
                                                              _,
                                                              newValue,
                                                            ) =>
                                                              handleSubSkillLevelChange(
                                                                value,
                                                                mainSkill,
                                                                subSkill,
                                                                newValue,
                                                              )
                                                            }
                                                            valueLabelDisplay="off"
                                                            // size="small"
                                                            sx={{
                                                              mt: -1,
                                                              width: "80%",
                                                            }}
                                                          />
                                                        </Box>
                                                      )}
                                                    </Box>
                                                  );
                                                },
                                              )}
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ),
                )}
              </Box>

              {/* Languages Section */}
              <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Language Requirements *
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Autocomplete
                  options={languagesOptions}
                  value={selectedLanguage}
                  onChange={(event, newValue) => {
                    setSelectedLanguage(newValue);
                  }}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Language"
                      placeholder="Add language"
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: 2 }}
                    />
                  )}
                />

                {selectedLanguage && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel>Fluency</InputLabel>
                      <Select
                        label="Fluency"
                        value={selectedFluency}
                        onChange={(e) => setSelectedFluency(e.target.value)}
                      >
                        {fluencyLevels.map((level) => (
                          <MenuItem key={level} value={level}>
                            {level}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      variant="contained"
                      onClick={() => {
                        if (selectedLanguage && selectedFluency) {
                          const isAlreadyAdded = languagesList.some(
                            (lang) => lang.name === selectedLanguage.name,
                          );

                          if (!isAlreadyAdded) {
                            const newLanguage = {
                              name: selectedLanguage.name,
                              fluency: selectedFluency,
                            };
                            const updatedLanguages = [
                              ...languagesList,
                              newLanguage,
                            ];
                            setLanguagesList(updatedLanguages);
                            if (setParentLanguages)
                              setParentLanguages(updatedLanguages);
                            if (setFormLanguages)
                              setFormLanguages(updatedLanguages);
                          }

                          setSelectedLanguage(null);
                          setSelectedFluency("");
                        }
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                )}

                {languagesList.length > 0 && (
                  <Box
                    sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}
                  >
                    {languagesList.map((lang, index) => (
                      <Chip
                        key={index}
                        label={`${lang.name} (${lang.fluency})`}
                        onDelete={() => {
                          const updatedLanguages = languagesList.filter(
                            (_, i) => i !== index,
                          );
                          setLanguagesList(updatedLanguages);
                          if (setParentLanguages)
                            setParentLanguages(updatedLanguages);
                          if (setFormLanguages)
                            setFormLanguages(updatedLanguages);
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Button variant="outlined" color="secondary" onClick={onBack}>
                  Back
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={
                    Object.keys(selectedSkills).length === 0 ||
                    selectedSoftSkill.length === 0
                  }
                >
                  Next
                </Button>
              </Box>
            </>
          )}

          <Dialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#6c5ce7",
                color: "white",
                mb: 2,
              }}
            >
              Select Skills
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{
                  color: "white",
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent
              sx={{
                display: "flex",
                height: "70vh", // fixed height for dialog content
                overflow: "hidden",
                // prevent overall overflow
              }}
            >
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                >
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : skillsData ? (
                <Box sx={{ display: "flex", width: "100%" }}>
                  {/* Left Tabs */}
                  <Tabs
                    orientation="vertical"
                    value={tab}
                    onChange={handleTabChange}
                    sx={{
                      borderRight: 1,
                      borderColor: "divider",
                      minWidth: 140,
                      flexShrink: 0,
                    }}
                  >
                    {tabDefs.map((t) => (
                      <Tab key={t.value} value={t.value} label={t.label} />
                    ))}
                  </Tabs>

                  {/* Scrollable skill area */}
                  <Box
                    sx={{
                      flex: 1,
                      pl: 4,
                      overflowY: "auto",
                      pr: 2,
                    }}
                  >
                    <Alert
                      severity="info"
                      sx={{
                        display: "flex",
                        // justifyContent: "center",
                        // alignItems: "center",
                        mb: 2,
                      }}
                    >
                      {" "}
                      Please select at least one skill and a maximum of 3 skills
                      in {tab}-have skill.
                    </Alert>
                    {skillsData[currentTabDef.skillKey]?.map(
                      (mainSkill, idx) => {
                        const skillKey = createSkillKey(tab, mainSkill);
                        const isSelected = !!selectedSkills[skillKey];
                        const subSkills =
                          skillsData[currentTabDef.specificKey]?.[idx] || [];
                        const selectedData = selectedSkills[skillKey] || {
                          level: 1,
                          subSkills: [],
                        };

                        return (
                          <Box
                            key={mainSkill}
                            sx={{
                              mb: 3,
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: isSelected
                                ? "#f5f5f5"
                                : "transparent",
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() =>
                                    handleMainSkillToggle(
                                      tab,
                                      mainSkill,
                                      subSkills,
                                    )
                                  }
                                  disabled={
                                    !isSelected &&
                                    getAllSelectedSkillNames().includes(
                                      mainSkill,
                                    )
                                  }
                                />
                              }
                              label={
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 600,
                                    color:
                                      !isSelected &&
                                      getAllSelectedSkillNames().includes(
                                        mainSkill,
                                      )
                                        ? "text.disabled"
                                        : "text.primary",
                                  }}
                                >
                                  {mainSkill}
                                </Typography>
                              }
                            />

                            {isSelected && (
                              <Box sx={{ ml: 4, mt: 1 }}>
                                <Box sx={{ width: "60%", mb: 2 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ mb: 1, fontWeight: 600 }}
                                  >
                                    Skill Level
                                  </Typography>
                                  <Slider
                                    value={selectedData.level}
                                    min={1}
                                    max={4}
                                    step={1}
                                    marks={skillLevels.map((label, idx) => ({
                                      value: idx + 1,
                                      label,
                                    }))}
                                    onChange={(_, newValue) =>
                                      handleLevelChange(
                                        tab,
                                        mainSkill,
                                        newValue,
                                      )
                                    }
                                    valueLabelDisplay="off"
                                  />
                                </Box>

                                {subSkills.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Sub-skills
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1,
                                        mt: 1,
                                      }}
                                    >
                                      {subSkills.map((subSkill) => {
                                        const subSkillData = (
                                          selectedData.subSkills || []
                                        ).find((s) =>
                                          typeof s === "string"
                                            ? s === subSkill
                                            : s.name === subSkill,
                                        );
                                        const isChecked = !!subSkillData;
                                        const subSkillLevel =
                                          typeof subSkillData === "object"
                                            ? subSkillData.level
                                            : selectedData.level || 1;

                                        return (
                                          <Box
                                            key={subSkill}
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              mb: 1,
                                              width: "100%",
                                            }}
                                          >
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={isChecked}
                                                  onChange={() =>
                                                    handleSubSkillToggle(
                                                      tab,
                                                      mainSkill,
                                                      subSkill,
                                                    )
                                                  }
                                                  size="small"
                                                />
                                              }
                                              label={
                                                <Typography variant="body2">
                                                  {subSkill}
                                                </Typography>
                                              }
                                            />
                                            {isChecked && (
                                              <Box
                                                sx={{
                                                  pl: 4,
                                                  pr: 2,
                                                  width: "80%",
                                                }}
                                              >
                                                <Slider
                                                  value={subSkillLevel}
                                                  min={1}
                                                  max={4}
                                                  step={1}
                                                  marks={skillLevels.map(
                                                    (label, idx) => ({
                                                      value: idx + 1,
                                                      label,
                                                    }),
                                                  )}
                                                  onChange={(_, newValue) =>
                                                    handleSubSkillLevelChange(
                                                      tab,
                                                      mainSkill,
                                                      subSkill,
                                                      newValue,
                                                    )
                                                  }
                                                  valueLabelDisplay="off"
                                                  // size="small"
                                                  sx={{ mt: -1, width: "60%" }}
                                                />
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      },
                    )}
                    {(!skillsData[currentTabDef.skillKey] ||
                      skillsData[currentTabDef.skillKey].length === 0) && (
                      <Typography color="text.secondary">
                        No skills available for this category.
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : null}
            </DialogContent>
            <DialogActions>
              {tab !== "could" && (
                <Button
                  variant="contained"
                  onClick={() => {
                    // Check if current tab has at least one skill selected
                    if (!hasCategorySelected(tab)) {
                      toast.warning(
                        `Please select at least one ${
                          tabDefs.find((t) => t.value === tab)?.label
                        } skill`,
                      );
                      return;
                    }
                    // Move to next tab
                    const currentIndex = tabDefs.findIndex(
                      (t) => t.value === tab,
                    );
                    if (currentIndex < tabDefs.length - 1) {
                      setTab(tabDefs[currentIndex + 1].value);
                    }
                  }}
                  disabled={!hasCategorySelected(tab)}
                >
                  Next
                </Button>
              )}
              {tab === "could" &&
                (() => {
                  const { allSelected, remaining } = allCategoriesSelected();

                  return (
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (!allSelected) {
                          toast.warning(
                            `Please select at least one ${remaining[0]}-have skill`,
                          );
                          return;
                        }
                        handleCloseDialog();
                      }}
                      disabled={!hasCategorySelected(tab) || !allSelected}
                    >
                      Done
                    </Button>
                  );
                })()}
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </>
  );
};

export default SetSkills;
